import { Result, TimeParts, TimeParseError } from "./timePicker.types";

/** Converts a number to a two-digit string with leading zero. */
export const toTwoDigits = (value: number): string =>
  value.toString().padStart(2, "0");

/** Formats time parts into HH:mm. */
export const formatTime = (time: TimeParts): string =>
  `${toTwoDigits(time.hours)}:${toTwoDigits(time.minutes)}`;

/** Parses free-form time input into hours/minutes with validation. */
export const parseTimeInput = (
  input: string,
): Result<TimeParts, TimeParseError> => {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return { ok: false, error: "empty" };
  }

  const groups = trimmed.split(/[^0-9]+/).filter(Boolean);
  let hoursPart = "";
  let minutesPart = "";

  if (groups.length >= 2) {
    hoursPart = groups[0];
    minutesPart = groups[1];
  } else if (groups.length === 1) {
    const digits = groups[0];
    if (digits.length <= 2) {
      hoursPart = digits;
      minutesPart = "0";
    } else if (digits.length === 3) {
      hoursPart = digits.slice(0, 1);
      minutesPart = digits.slice(1);
    } else {
      hoursPart = digits.slice(0, 2);
      minutesPart = digits.slice(2, 4);
    }
  } else {
    return { ok: false, error: "invalid" };
  }

  const hours = Number(hoursPart);
  const minutes = Number(minutesPart || "0");
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return { ok: false, error: "invalid" };
  }

  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return { ok: false, error: "out-of-range" };
  }

  return { ok: true, value: { hours, minutes } };
};

/** Parses a strictly formatted HH:mm value. */
export const parseFormattedTime = (
  value?: string,
): Result<TimeParts, "invalid"> => {
  if (!value || !/^\d{2}:\d{2}$/.test(value)) {
    return { ok: false, error: "invalid" };
  }
  const parsed = parseTimeInput(value);
  return parsed.ok ? parsed : { ok: false, error: "invalid" };
};
